import type {
  DimensionOption,
  QueryResult,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import { BaseClient } from '../base-client';

/**
 * Typed client for the Analytics API (`/api/analytics/*`) — the custom-report
 * builder's two calls.
 *
 * Gated server-side by `report.view` alone (which rows come back follows the
 * caller's user.view and customer.view), and it is enough to RUN a report: someone with
 * it can open what was shared with them, change the filters and dates while
 * looking at it, and export. Saving is what costs `report.edit`, and that lives
 * on `ReportDefinitionsClient`.
 *
 * Beyond that one id nobody is refused: the scope is passed into the report's
 * own SQL, which fails CLOSED on a scope it does not recognise. So an empty
 * result is a reachable, legitimate state — treat it as data, not as impossible.
 *
 * The catalog type is `ReportModel`, NOT `ReportModelSource`: the server
 * projects the physical half away before it leaves. Typing it as the client
 * shape here is what makes a leak a compile error rather than a code review.
 */
export class AnalyticsClient extends BaseClient {
  /** The subject areas this caller may report on, and their vocabulary. */
  models(signal?: AbortSignal): Promise<{ data: ReportModel[] }> {
    return this.request('/api/analytics/models', { signal });
  }

  /**
   * The MEMBERS of one filterable dimension — what its filter picker offers.
   *
   * NOT part of the catalog, and deliberately: `models()` carries VOCABULARY —
   * that a report can be split by Customer — while this carries the customers
   * themselves, which are tenant data, are searched a page at a time, and are
   * scoped to what the caller may see. Shipping them with the models would put
   * the whole directory in the browser of anyone who opened the builder.
   *
   * The values come from the report's own rows, so this list is exactly the set
   * of values that would change what is on screen. It is period-bound for the
   * same reason — hence `from`/`to`, which are the report's own.
   *
   * `keys` resolves EXACT members instead of listing, for a saved report whose
   * filters hold ids: without it a reopened report shows a uuid in its chip.
   * The period still binds the security clause, but not the rows, so a value
   * chosen for January still reads correctly under a February report.
   */
  dimensionValues(
    modelId: string,
    dimensionId: string,
    params: {
      from: string;
      to: string;
      q?: string;
      keys?: readonly string[];
      limit?: number;
    },
    signal?: AbortSignal,
  ): Promise<{ data: DimensionOption[]; meta: { total: number; limit: number; offset: number } }> {
    return this.request(
      `/api/analytics/models/${encodeURIComponent(modelId)}/dimensions/${encodeURIComponent(dimensionId)}/values`,
      {
        // REPEATED `key`, never a joined string: a member's key is whatever the
        // dimension produced, and a derived bucket's is a word that may hold a
        // comma. See the server's `dimensionValuesQuerySchema`.
        query: {
          from: params.from,
          to: params.to,
          q: params.q,
          key: params.keys ? [...params.keys] : undefined,
          limit: params.limit,
        },
        signal,
      },
    );
  }

  /**
   * Run a spec.
   *
   * A POST for a read: the spec is a nested object with arrays in it, and a
   * saved report will send the same shape. `signal` is threaded so the builder
   * can abandon an in-flight query when the spec changes again.
   *
   * `includeSql` asks for the compiled statement back in `meta.sql`. It costs
   * `report.share` on top of the id this endpoint already needs, and asking
   * without it is a `FORBIDDEN` `ApiError` rather than a result with the field
   * quietly missing — see the server's `AnalyticsService.run`.
   */
  query(
    spec: ReportSpec,
    signal?: AbortSignal,
    options: { includeSql?: boolean } = {},
  ): Promise<QueryResult> {
    // Absent, not `sql=false`, when nothing was asked for: an option that is
    // off is an option nobody mentioned.
    const path = options.includeSql ? '/api/analytics/query?sql=true' : '/api/analytics/query';
    return this.request<{ data: QueryResult['rows']; meta: QueryResult['meta'] }>(path, {
      method: 'POST',
      body: spec,
      signal,
    }).then(({ data, meta }) => ({ rows: data, meta }));
  }

  /**
   * Run a spec and get an Excel workbook back instead of rows.
   *
   * `name` is the filename stem the browser saves under. The server cannot
   * know it — a spec carries no name, and the same spec is a saved report to
   * one caller and an unsaved draft to another — so the caller that DOES know
   * sends it.
   *
   * Paging is not sent and would not be honoured: an export is the whole
   * answer, never the page that happened to be on screen. Over the server's
   * row cap this rejects with a `BUSINESS_RULE` `ApiError` naming the size and
   * the cap, rather than returning a shorter file.
   */
  exportReport(
    spec: ReportSpec,
    options: { name?: string; signal?: AbortSignal } = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/analytics/export', {
      method: 'POST',
      body: spec,
      query: { name: options.name },
      signal: options.signal,
    });
  }
}
